import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapControls, useV5ThemeClassName } from '@routific/hive/v5';
import type { V5MapControlsPlacement } from '@routific/hive/v5';

export interface MapControlItem {
  key: string;
  // i18n key (components namespace) used as both the visible tooltip and the
  // control's accessible name.
  title: string;
  icon: React.ReactNode;
  disabled?: boolean;
  // Delay before the tooltip appears, in seconds. Converted to milliseconds for
  // the v5 Tooltip.
  tooltipDelay?: number;
  onClick?: () => void;
}

interface Props {
  buttons: MapControlItem[];
  dataCy: string;
  // Distance to hold clear of the trailing (right) edge — e.g. an open right
  // panel's width — so the cluster stays out of its way.
  offsetEnd?: number | string;
  placement?: V5MapControlsPlacement;
  'aria-label'?: string;
}

// An intermediate wrapper over the Hive v5 `MapControls` cluster: it turns a
// simple button config into `MapControls.Button` children, resolves i18n keys
// for titles/tooltips, and applies the v5 theme class here — context flows
// through portals, so the controls render even when the map lives in a
// portalled surface (e.g. the add-order modal) outside the app-root
// V5ThemeProvider's DOM subtree.
const MapControlBar: React.FC<Props> = ({
  buttons,
  dataCy,
  offsetEnd = 0,
  placement = 'top-end',
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation('components');
  const themeClassName = useV5ThemeClassName();

  return (
    <MapControls
      className={themeClassName}
      placement={placement}
      offsetEnd={offsetEnd}
      data-cy={dataCy}
      data-testid={dataCy}
      aria-label={ariaLabel ?? t('mapToolbar.ariaLabel')}
    >
      {buttons.map(({
        key, title, icon, disabled, tooltipDelay, onClick,
      }) => (
        <MapControls.Button
          key={key}
          icon={icon}
          aria-label={t(title)}
          tooltip={t(title)}
          tooltipEnterDelay={tooltipDelay != null ? tooltipDelay * 1000 : undefined}
          disabled={disabled}
          onClick={onClick}
          data-cy={`toolbar-${key}`}
          data-testid={`toolbar-${key}`}
        />
      ))}
    </MapControls>
  );
};

export default MapControlBar;
